"use client";

import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { getPatientWithMainMemberDetails } from "../api/clinic/consultationApi.js";

const ConsultationContext = createContext(null);

export const ConsultationProvider = ({ children }) => {
  const [isInConsultation, setIsInConsultation] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [stepDrafts, setStepDrafts] = useState({});
  const [consultationData, setConsultationData] = useState({
    consultationId: null,
    appointmentId: null,
    patientId: null,
    patient: null,
    serviceId: null,
    baseServiceId: null,
    activeWorkflowServiceId: null,
    serviceName: "",
    serviceCode: "",
    dependentCode: null,
    appointmentDate: null,
    appointmentTime: null,
    returnedFromWorkflow: false,
  });

  const startConsultation = useCallback((data) => {
    setStepDrafts({});
    setConsultationData((prev) => ({
      consultationId: null,
      appointmentId: null,
      patientId: null,
      patient: null,
      serviceId: null,
      baseServiceId: null,
      activeWorkflowServiceId: null,
      serviceName: "",
      serviceCode: "",
      dependentCode: null,
      appointmentDate: null,
      appointmentTime: null,
      returnedFromWorkflow: false,
      ...(prev || {}),
      ...(data || {}),
    }));
    setCurrentStep(1);
    setIsInConsultation(true);
  }, []);

  const endConsultation = useCallback(() => {
    setIsInConsultation(false);
    setCurrentStep(1);
    setStepDrafts({});
    setConsultationData({
      consultationId: null,
      appointmentId: null,
      patientId: null,
      patient: null,
      serviceId: null,
      baseServiceId: null,
      activeWorkflowServiceId: null,
      serviceName: "",
      serviceCode: "",
      dependentCode: null,
      appointmentDate: null,
      appointmentTime: null,
    });
  }, []);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, 10));
  }, []);

  const previousStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, []);

  const updateConsultationData = useCallback((updates) => {
    setConsultationData((prev) => ({ ...prev, ...updates }));
  }, []);

  const setStepDraft = useCallback((key, value) => {
    if (!key) return;
    setStepDrafts((prev) => ({
      ...(prev || {}),
      [key]: typeof value === 'function' ? value(prev?.[key]) : value,
    }));
  }, []);

  const getStepDraft = useCallback((key, fallback = null) => {
    if (!key) return fallback;
    return (stepDrafts && Object.prototype.hasOwnProperty.call(stepDrafts, key))
      ? stepDrafts[key]
      : fallback;
  }, [stepDrafts]);

  const clearStepDrafts = useCallback(() => {
    setStepDrafts({});
  }, []);

  // Fetch main member details when patientId changes
  useEffect(() => {
    const patientId = consultationData?.patientId;
    if (!patientId || consultationData?.patient?.mainMember) {
      return;
    }

    let cancelled = false;
    const fetchMainMemberDetails = async () => {
      try {
        const patientWithMainMember = await getPatientWithMainMemberDetails(patientId);
        if (cancelled) return;

        if (patientWithMainMember && patientWithMainMember.mainMember) {
          setConsultationData((prev) => ({
            ...prev,
            patient: {
              ...prev.patient,
              mainMember: patientWithMainMember.mainMember,
            },
          }));
        }
      } catch (error) {
        console.error('Failed to fetch main member details:', error);
      }
    };

    fetchMainMemberDetails();

    return () => {
      cancelled = true;
    };
  }, [consultationData?.patientId, consultationData?.patient?.mainMember]);

  const value = {
    isInConsultation,
    setIsInConsultation,
    currentStep,
    consultationData,
    stepDrafts,
    startConsultation,
    endConsultation,
    goToStep,
    nextStep,
    previousStep,
    updateConsultationData,
    setStepDraft,
    getStepDraft,
    clearStepDrafts,
  };

  return (
    <ConsultationContext.Provider value={value}>
      {children}
    </ConsultationContext.Provider>
  );
};

ConsultationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useConsultation = () => {
  const context = useContext(ConsultationContext);
  if (!context) {
    throw new Error("useConsultation must be used within a ConsultationProvider");
  }
  return context;
};
